import { Suspense, lazy } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Lazy load toasters (not needed immediately)
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
import { PeriodProvider } from "@/contexts/PeriodContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RoleBasedRoute } from "@/components/RoleBasedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { useVersionCheck } from "@/hooks/useVersionCheck";
// Lazy load page components with retry logic for chunk loading failures
const Home = lazyWithRetry(() => import("./pages/Home"), "Home");
const Auth = lazyWithRetry(() => import("./pages/Auth"), "Auth");
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"), "ResetPassword");
const Accounts = lazyWithRetry(() => import("./pages/Accounts"), "Accounts");
const Worksheet = lazyWithRetry(() => import("./pages/Worksheet"), "Worksheet");
const Sales = lazyWithRetry(() => import("./pages/Sales"), "Sales");
const Expenditure = lazyWithRetry(() => import("./pages/Expenditure"), "Expenditure");
const Bills = lazyWithRetry(() => import("./pages/Bills"), "Bills");
const Receivables = lazyWithRetry(() => import("./pages/Receivables"), "Receivables");
const Payables = lazyWithRetry(() => import("./pages/Payables"), "Payables");
const CashFlow = lazyWithRetry(() => import("./pages/CashFlow"), "CashFlow");
const CashFlowBreakdownDetail = lazyWithRetry(() => import("./pages/CashFlowBreakdownDetail"), "CashFlowBreakdownDetail");
const CashManagement = lazyWithRetry(() => import("./pages/CashManagement"), "CashManagement");
const ProfitDistributionPage = lazyWithRetry(() => import("./pages/ProfitDistributionPage"), "ProfitDistribution");
const TrialBalance = lazyWithRetry(() => import("./pages/TrialBalance"), "TrialBalance");
const Reports = lazyWithRetry(() => import("./pages/Reports"), "Reports");
const Settings = lazyWithRetry(() => import("./pages/Settings"), "Settings");
const NotFound = lazyWithRetry(() => import("./pages/NotFound"), "NotFound");
const TransactionDetail = lazyWithRetry(() => import("./pages/TransactionDetail"), "TransactionDetail");
const WithholdingTaxReport = lazyWithRetry(() => import("./pages/WithholdingTaxReport"), "WithholdingTaxReport");
const Wages = lazyWithRetry(() => import("./pages/Wages"), "Wages");
const LoanManagement = lazyWithRetry(() => import("./pages/LoanManagement"), "LoanManagement");
const InsuranceCompanyDetail = lazyWithRetry(() => import("./pages/InsuranceCompanyDetail"), "InsuranceCompanyDetail");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  },
});

// Simple loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Version check wrapper component
const VersionChecker = ({ children }: { children: React.ReactNode }) => {
  useVersionCheck();
  return <>{children}</>;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
          <VersionChecker>
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/home" element={<Home />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <PeriodProvider>
                          <Layout>
                            <Suspense fallback={<PageLoader />}>
                              <Routes>
                                <Route path="/" element={<Worksheet />} />
                                <Route path="/accounts" element={<Accounts />} />
                                <Route path="/worksheet" element={<Navigate to="/" replace />} />
                                <Route path="/sales" element={<Sales />} />
                                <Route path="/expenditure" element={<Expenditure />} />
                                <Route path="/bills" element={<Bills />} />
                                <Route path="/receivables" element={<Receivables />} />
                                <Route path="/payables" element={<Payables />} />
                                <Route path="/wages" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><Wages /></RoleBasedRoute>} />
                                <Route path="/loans" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><LoanManagement /></RoleBasedRoute>} />
                                <Route path="/cash-flow" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><CashFlow /></RoleBasedRoute>} />
                                <Route path="/cash-flow/breakdown/:breakdownType" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><CashFlowBreakdownDetail /></RoleBasedRoute>} />
                                <Route path="/cash-management" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><CashManagement /></RoleBasedRoute>} />
                                <Route path="/transactions" element={<Navigate to="/sales" replace />} />
                                <Route path="/transaction/:id" element={<TransactionDetail />} />
                                <Route path="/profit-distribution" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><ProfitDistributionPage /></RoleBasedRoute>} />
                                <Route path="/trial-balance" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><TrialBalance /></RoleBasedRoute>} />
                                <Route path="/reports" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><Reports /></RoleBasedRoute>} />
                                <Route path="/wht-report" element={<Navigate to="/reports" replace />} />
                                <Route path="/settings" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><Settings /></RoleBasedRoute>} />
                                <Route path="/settings/insurance-company/:id" element={<RoleBasedRoute adminOnly restrictedRoles={['user']}><InsuranceCompanyDetail /></RoleBasedRoute>} />
                                <Route path="*" element={<NotFound />} />
                              </Routes>
                            </Suspense>
                          </Layout>
                        </PeriodProvider>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </VersionChecker>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
